import { useNotify, useRecordContext, useUpdate } from "ra-core";
import { Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal } from "../types";

/**
 * Move a lead to another stage from the lead itself.
 *
 * On the desktop board a stage is changed by dragging a card between columns.
 * That is the only way it could be changed at all, so on a phone -- where the
 * board is a list -- a lead could be read but never moved, and even on a
 * desktop you had to leave the record and go back to the board.
 *
 * A stage is one value from a short list, which is a menu. Optimistic, because
 * the whole point is that it feels like moving a card: the label changes on the
 * tap and rolls back only if the server refuses.
 */
export const DealStageSelector = ({
  className,
}: {
  className?: string;
}) => {
  const record = useRecordContext<Deal>();
  const { dealStages } = useConfigurationContext();
  const [update, { isPending }] = useUpdate();
  const notify = useNotify();

  if (!record) return null;

  const current = dealStages.find((s) => s.value === record.stage);

  const setStage = (stage: string) => {
    if (stage === record.stage) return;
    update(
      "deals",
      { id: record.id, data: { stage }, previousData: record },
      {
        mutationMode: "optimistic",
        onError: (error) =>
          notify(
            error instanceof Error ? error.message : "Could not change the stage",
            { type: "error" },
          ),
      },
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={isPending}
          className={cn("justify-between gap-2", className)}
        >
          {current?.label ?? record.stage}
          <ChevronDown className="size-4 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-48">
        {dealStages.map((stage) => (
          <DropdownMenuItem
            key={stage.value}
            // Comfortable to tap: a menu that is only ever opened on a phone to
            // change one value should not need aiming.
            className="h-11 gap-2"
            onSelect={() => setStage(stage.value)}
          >
            <Check
              className={cn(
                "size-4",
                stage.value === record.stage ? "opacity-100" : "opacity-0",
              )}
            />
            {stage.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
